import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import { Ellipsis } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'react-hot-toast'
import { useLocation, useOutletContext } from 'react-router'

import type { Invite } from '@ankor-io/common/types/account'

import { useUserContext } from '@/providers/user/useUserContext'
import { Badge } from '@/ui-components/Badge'
import { Dropdown, DropdownButton, DropdownItem, DropdownMenu } from '@/ui-components/Dropdown'
import Loading from '@/ui-components/Loading'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/ui-components/Table'
import RevokeInviteModal from '@/views/components/Users/RevokeInviteModal'

const getStatusColor = (status: string) => {
  switch (status) {
    case 'sending':
      return 'blue'
    case 'sent':
      return 'indigo'
    case 'pending':
      return 'yellow'
    case 'accepted':
      return 'green'
    case 'rejected':
      return 'red'
    case 'expired':
      return 'gray'
    default:
      return 'gray'
  }
}

const Invites = () => {
  const { getAccessToken } = useKindeAuth()
  const location = useLocation()
  const { activeOrganizationId, activeConnectedAccountId } = useUserContext()
  const { newlyInvitedUser } = useOutletContext<{ newlyInvitedUser: Invite | null }>()

  const [isLoading, setIsLoading] = useState(true)

  const [organizationInvites, setOrganizationInvites] = useState<Invite[]>([])
  const [connectedAccountInvites, setConnectedAccountInvites] = useState<Invite[]>([])
  const [selectedInvite, setSelectedInvite] = useState<Invite | null>(null)
  const [isRevokeInviteModalOpen, setIsRevokeInviteModalOpen] = useState(false)

  const isOrganizationUserPage = !location.pathname.includes('vessel-users')

  useEffect(() => {
    const getInvites = async () => {
      if (!activeOrganizationId) {
        return
      }

      setIsLoading(true)
      try {
        const res = await fetch(`/api/pay/org/${activeOrganizationId}/users/invites`, {
          headers: {
            Authorization: `Bearer ${await getAccessToken()}`,
          },
        })

        if (!res.ok) {
          console.error(res)
          throw new Error('Failed to fetch users')
        }

        const data = (await res.json()) as { invites: Invite[] }
        setOrganizationInvites(data.invites)
      } catch (error) {
        console.error('Error fetching invites:', error)
        setOrganizationInvites([])
      } finally {
        setIsLoading(false)
      }
    }

    const getConnectedInvites = async () => {
      if (!activeOrganizationId || !activeConnectedAccountId) {
        return
      }

      setIsLoading(true)
      try {
        const res = await fetch(
          `/api/pay/org/${activeOrganizationId}/connected-account/${activeConnectedAccountId}/users/invites`,
          {
            headers: {
              Authorization: `Bearer ${await getAccessToken()}`,
            },
          },
        )

        if (!res.ok) {
          console.error(res)
          throw new Error('Failed to fetch users')
        }

        const data = (await res.json()) as { invites: Invite[] }
        setConnectedAccountInvites(data.invites)
      } catch (error) {
        console.error('Error fetching invites:', error)
        setConnectedAccountInvites([])
      } finally {
        setIsLoading(false)
      }
    }

    if (isOrganizationUserPage) {
      getInvites()
    } else {
      getConnectedInvites()
    }
  }, [activeConnectedAccountId, activeOrganizationId, getAccessToken, isOrganizationUserPage])

  useEffect(() => {
    if (newlyInvitedUser) {
      setOrganizationInvites((prevInvites) => [newlyInvitedUser, ...prevInvites])
      setConnectedAccountInvites((prevInvites) => [newlyInvitedUser, ...prevInvites])
    }
  }, [newlyInvitedUser])

  const openRevokeInviteModal = (invite: Invite) => {
    setSelectedInvite(invite)
    setIsRevokeInviteModalOpen(true)
  }

  const closeRevokeInviteModal = () => {
    setIsRevokeInviteModalOpen(false)
    setSelectedInvite(null)
  }

  const handleRevokeInvite = async (invite: Invite) => {
    if (!invite || !activeOrganizationId) {
      return
    }

    const revokeInvite = async () => {
      const res = await fetch(`/api/pay/org/${activeOrganizationId}/users/invite/${invite.id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${await getAccessToken()}`,
        },
      })

      setIsRevokeInviteModalOpen(false)
      if (!res.ok) {
        throw new Error('Failed to revoke invite')
      }

      // Remove the revoked invite from the list
      setOrganizationInvites((prevInvites) => prevInvites.filter((i) => i.id !== invite.id))
      setConnectedAccountInvites((prevInvites) => prevInvites.filter((i) => i.id !== invite.id))
    }

    toast.promise(revokeInvite(), {
      loading: `Revoking invite for ${invite?.email}...`,
      success: 'Invite revoked successfully',
      error: 'Failed to revoke invite',
    })
  }

  return (
    <>
      <Table className="w-[calc(100vw-3rem)] md:w-auto">
        <TableHead>
          <TableRow>
            <TableHeader>Email</TableHeader>
            <TableHeader>Roles</TableHeader>
            <TableHeader>Status</TableHeader>
            <TableHeader>Actions</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {(isOrganizationUserPage ? organizationInvites : connectedAccountInvites).map((invite) => (
            <TableRow key={invite.id}>
              <TableCell>{invite.email}</TableCell>
              <TableCell
                className="capitalize"
                data-testid="roles"
              >
                {invite.roles.join(', ')}
              </TableCell>
              <TableCell>
                <Badge color={getStatusColor(invite.status)}>
                  {invite.status.charAt(0).toUpperCase() + invite.status.slice(1)}
                </Badge>
              </TableCell>
              <TableCell>
                {['sending', 'sent', 'pending'].includes(invite.status) && (
                  <div className="relative z-10 pointer-events-auto">
                    <Dropdown>
                      <DropdownButton plain>
                        <Ellipsis className="size-4" />
                      </DropdownButton>
                      <DropdownMenu anchor="bottom end">
                        <DropdownItem onClick={() => openRevokeInviteModal(invite)}>Revoke invite</DropdownItem>
                      </DropdownMenu>
                    </Dropdown>
                  </div>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {isLoading ? (
        <div className="flex h-full items-center justify-center py-12">
          <Loading />
        </div>
      ) : (
        (isOrganizationUserPage ? organizationInvites : connectedAccountInvites).length === 0 && (
          <div className="flex h-full items-center justify-center py-12">
            <p className="text-sm text-gray-600 dark:text-gray-400">No pending invites</p>
          </div>
        )
      )}

      {selectedInvite && activeOrganizationId && (
        <RevokeInviteModal
          isOpen={isRevokeInviteModalOpen}
          onClose={closeRevokeInviteModal}
          selectedInvite={selectedInvite}
          onSubmit={handleRevokeInvite}
        />
      )}
    </>
  )
}

export default Invites
